import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { PageHeader, StatBadge, GhostButton, GoldButton } from "@/components/admin/AdminLayout";
import { useLang } from "@/lib/lang";
import { toast } from "sonner";
import { ArrowLeft, Handshake, Wallet, ShieldCheck, ShieldOff, XCircle, CheckCircle2, Mail, Phone, Hash, User as UserIcon, MapPin, FileText, Copy, ExternalLink } from "lucide-react";
import { BlockModal } from "@/pages/admin/BlockedDashboard";

const TYPES = {
  hotel: "Hotel", travel_agency: "Agência de viagens", fleet: "Frota",
};

export default function PartnerDetail() {
  const { id } = useParams();
  const { t } = useLang();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [blockOpen, setBlockOpen] = useState(false);

  const load = () => {
    setLoading(true);
    api.get(`/admin/partners/${id}`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const setStatus = async (status) => {
    try {
      await api.patch(`/admin/partners/${id}`, { status });
      toast.success(t(`status_${status}`) || status);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const setFlag = async (field, value) => {
    try {
      await api.patch(`/admin/partners/${id}`, { [field]: value });
      toast.success(t("toast_saved") || "Guardado");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const copy = (text) => {
    try {
      if (navigator.clipboard?.writeText) {
        navigator.clipboard.writeText(text).then(() => toast.success("Copiado")).catch(() => toast.error("Cópia bloqueada"));
      } else {
        toast.error("Clipboard indisponível");
      }
    } catch { toast.error("Cópia bloqueada"); }
  };

  if (loading) return <div className="p-10 text-slate-500">{t("loading")}</div>;
  if (!data) return <div className="p-10 text-slate-500">Sem dados.</div>;

  const { partner, recent_bookings: bookings, kpis } = data;

  return (
    <div className="p-10 max-w-[1600px]" data-testid="partner-detail-page">
      <div className="mb-6">
        <Link to="/admin/partners" className="text-xs uppercase tracking-widest text-slate-600 hover:text-[#f59e0b] inline-flex items-center gap-1 font-bold">
          <ArrowLeft className="w-3.5 h-3.5" /> {t("nav_partners")}
        </Link>
      </div>

      <PageHeader
        eyebrow={TYPES[partner.type] || partner.type}
        title={partner.company_name}
        description={`NIF ${partner.nif} · ${partner.address || "—"}`}
      >
        <StatBadge status={partner.status} />
        {partner.status !== "approved" && partner.status !== "suspended" && (
          <GoldButton onClick={() => setStatus("approved")} data-testid="partner-approve">
            <CheckCircle2 className="w-3.5 h-3.5" /> {t("approve")}
          </GoldButton>
        )}
        {partner.status === "approved" && (
          <GhostButton onClick={() => setBlockOpen(true)} data-testid="partner-block-btn" className="!border-red-300 !text-red-700 hover:!bg-red-50">
            <ShieldOff className="w-3.5 h-3.5" /> Bloquear
          </GhostButton>
        )}
        {partner.status === "suspended" && (
          <>
            <GoldButton
              data-testid="partner-unblock-btn"
              onClick={async () => {
                try {
                  await api.post(`/admin/partners/${id}/unblock`);
                  toast.success("Parceiro desbloqueado");
                  load();
                } catch (e) { toast.error(formatApiError(e)); }
              }}
            >
              <ShieldCheck className="w-3.5 h-3.5" /> Desbloquear
            </GoldButton>
            {partner.block_reason && (
              <div className="text-xs text-red-700 max-w-xs">
                <span className="font-semibold uppercase">Motivo:</span> {partner.block_reason}
              </div>
            )}
          </>
        )}
      </PageHeader>
      <BlockModal
        open={blockOpen}
        onClose={() => setBlockOpen(false)}
        entityLabel={`parceiro ${partner.company_name}`}
        onConfirm={async (reason) => {
          await api.post(`/admin/partners/${id}/block`, { reason });
          toast.success("Parceiro bloqueado");
          load();
        }}
      />

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <Kpi label={t("bookings_count")} value={kpis.bookings_count} data-testid="kpi-bookings" />
        <Kpi label="Concluídas" value={kpis.bookings_completed} />
        <Kpi label="Receita cliente" value={`€ ${kpis.revenue_client_paid.toFixed(2)}`} data-testid="kpi-revenue" />
        <Kpi label="Ganho do parceiro" value={`€ ${kpis.revenue_partner_earning.toFixed(2)}`} accent />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left column: Info + link + docs */}
        <aside className="lg:col-span-1 space-y-4">
          <Card title="Informação da empresa" icon={Handshake}>
            <Row icon={UserIcon} label="Contacto" value={partner.contact_person} />
            <Row icon={Mail} label="Email" value={partner.email} />
            <Row icon={Phone} label="Telefone" value={partner.phone} />
            <Row icon={Hash} label="Slug" value={<span className="font-mono text-[#f59e0b] font-bold">{partner.slug}</span>} />
            <Row icon={MapPin} label="Morada" value={partner.address} />
          </Card>

          <Card title="Comissão & faturação" icon={Wallet}>
            <Row label="Comissão hotel" value={<span className="text-[#f59e0b] font-mono font-bold">{partner.commission_pct}% · {partner.commission_direction === "partner_earns" ? "parceiro ganha" : "desconto ao cliente"}</span>} />
            <Row label="Ciclo faturação" value={partner.billing_cycle === "monthly" ? "Mensal" : "Por reserva"} />
            <Row label="Limite crédito" value={`€ ${(partner.credit_limit_eur || 0).toFixed(0)}`} />
            <Row label="Email extratos" value={partner.statement_email || partner.email} />
            <div className="border border-slate-100 p-2.5 bg-slate-50/40 mt-2">
              <div className="text-[11px] uppercase tracking-widest text-slate-700 font-bold mb-1">Comissão DiTours (%)</div>
              <div className="text-[10px] text-slate-500 mb-2">Deixe vazio para usar o default global. Se definido aqui, sobrepõe-se ao default.</div>
              <input
                type="number" min="0" step="0.1"
                defaultValue={partner.ditours_commission_pct ?? ""}
                placeholder="—"
                onBlur={(e) => setFlag("ditours_commission_pct", e.target.value === "" ? null : Number(e.target.value))}
                data-testid="partner-ditours-commission"
                className="input-dt w-24 h-8 text-sm"
              />
            </div>
          </Card>

          <Card title="Configuração comercial" icon={ShieldCheck}>
            <ToggleRow
              label="Cliente pode pagar diretamente"
              hint="Se ativo, o widget mostra métodos de pagamento (MB Way, Multibanco, Cartão) ao cliente final. Caso contrário, cobrança mensal ao parceiro."
              value={!!partner.allow_client_payment}
              onChange={(v) => setFlag("allow_client_payment", v)}
              testId="partner-allow-client-payment-toggle"
            />
            <ToggleRow
              label="Permitir fatura genérica (sem nome)"
              hint="Se desativado, o cliente é obrigado a preencher Nome (e opcionalmente NIF) na fatura."
              value={partner.invoice_allow_generic !== false}
              onChange={(v) => setFlag("invoice_allow_generic", v)}
              testId="partner-invoice-generic-toggle"
            />
            <div className="border border-slate-100 p-2.5 bg-slate-50/40">
              <div className="text-[11px] uppercase tracking-widest text-slate-700 font-bold mb-1">Antecedência mínima (horas)</div>
              <div className="text-[10px] text-slate-500 mb-2">Aplica-se a todas as reservas do parceiro. A antecedência efectiva será o máximo entre este valor, o do tour e o da categoria de veículo.</div>
              <input
                type="number" min="0" step="0.5"
                defaultValue={partner.min_advance_hours ?? 0}
                onBlur={(e) => setFlag("min_advance_hours", Number(e.target.value) || 0)}
                data-testid="partner-min-advance"
                className="input-dt w-24 h-8 text-sm"
              />
            </div>
          </Card>

          <Card title="URLs & Subdomínio white-label" icon={ExternalLink}>
            <div className="text-[10px] uppercase tracking-widest text-slate-600 font-bold mb-1">1. Link directo (funciona já)</div>
            <div className="flex items-center gap-2">
              <code className="flex-1 text-[10px] font-mono bg-slate-50 border border-slate-200 p-2 truncate">{window.location.origin}/?partner={partner.slug}</code>
              <button onClick={() => copy(`${window.location.origin}/?partner=${partner.slug}`)} className="text-slate-500 hover:text-[#f59e0b]" data-testid="copy-partner-querystring"><Copy className="w-3 h-3" /></button>
            </div>
            <a href={`${window.location.origin}/?partner=${partner.slug}`} target="_blank" rel="noreferrer" className="mt-1 block text-center text-[10px] text-[#f59e0b] hover:underline">Pré-visualizar →</a>

            <div className="text-[10px] uppercase tracking-widest text-slate-600 font-bold mb-1 mt-4">2. Subdomínio white-label (aparência premium)</div>
            <div className="flex items-center gap-2">
              <code className="flex-1 text-[10px] font-mono bg-amber-50 border border-amber-200 p-2 truncate text-amber-800">https://{partner.slug}.ditours.pt</code>
              <button onClick={() => copy(`https://${partner.slug}.ditours.pt`)} className="text-slate-500 hover:text-[#f59e0b]" data-testid="copy-partner-subdomain"><Copy className="w-3 h-3" /></button>
            </div>
            <details className="mt-2">
              <summary className="text-[10px] uppercase tracking-widest text-slate-500 cursor-pointer hover:text-[#f59e0b]">Como activar o subdomínio ↓</summary>
              <div className="text-[10px] text-slate-600 mt-2 leading-relaxed border border-slate-200 bg-slate-50 p-2 space-y-1">
                <p><strong>Configuração DNS (uma vez para todos os parceiros):</strong></p>
                <p>1. No painel DNS da <code>ditours.pt</code> (Cloudflare/registrador), adicione um wildcard CNAME:</p>
                <p><code className="bg-white border px-1">*.ditours.pt CNAME tours-command.preview.emergentagent.com</code></p>
                <p>2. Assim que activo, <strong>qualquer</strong> subdomínio (ex: <code>{partner.slug}.ditours.pt</code>, <code>hotel-x.ditours.pt</code>) fica automaticamente ligado a esta landing e detecta o parceiro pelo slug.</p>
                <p className="text-amber-700">⚠️ Requer também SSL wildcard (*.ditours.pt) no reverse-proxy — configure via Cloudflare (Universal SSL grátis) ou Emergent Deploy.</p>
              </div>
            </details>

            <div className="text-[10px] uppercase tracking-widest text-slate-600 font-bold mb-1 mt-4">3. Slug (identificador interno · editável)</div>
            <SlugEditor
              current={partner.slug}
              companyName={partner.company_name}
              onSave={(newSlug) => setFlag("slug", newSlug)}
              onCopy={copy}
            />
            <p className="text-[10px] text-slate-500 mt-1">Usado em <code>?partner=SLUG</code>, no subdomínio <code>SLUG.ditours.pt</code> e nas páginas embed. Só letras minúsculas, números e hífens (2-40 caracteres).</p>

            {partner.api_key && (
              <>
                <div className="text-[10px] uppercase tracking-widest text-slate-600 font-bold mt-4 mb-1">API Key</div>
                <div className="flex items-center gap-2">
                  <code className="flex-1 text-[10px] font-mono bg-slate-50 border border-slate-200 p-2 truncate text-emerald-700">{partner.api_key}</code>
                  <button onClick={() => copy(partner.api_key)} className="text-slate-500 hover:text-[#f59e0b]"><Copy className="w-3 h-3" /></button>
                </div>
              </>
            )}
          </Card>

          <Card title="Documentos" icon={FileText}>
            <DocLink label="Certidão comercial" url={partner.doc_commercial_certificate} />
            <DocLink label="Contrato assinado" url={partner.doc_signed_contract} />
          </Card>

          {(partner.markup || partner.branding) && (
            <Card title="Branding & Markup" icon={ShieldCheck}>
              {partner.branding?.primary_color && <Row label="Cor primária" value={<span className="inline-flex items-center gap-2"><span className="w-4 h-4 border border-slate-200" style={{ background: partner.branding.primary_color }} /> {partner.branding.primary_color}</span>} />}
              {partner.branding?.accent_color && <Row label="Cor acento" value={<span className="inline-flex items-center gap-2"><span className="w-4 h-4 border border-slate-200" style={{ background: partner.branding.accent_color }} /> {partner.branding.accent_color}</span>} />}
              {partner.markup && Object.keys(partner.markup).length > 0 && (
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-slate-600 font-bold mb-1">Markups</div>
                  {Object.entries(partner.markup).map(([k, v]) => (
                    <div key={k} className="flex justify-between text-xs py-0.5"><span className="text-slate-700">{k}</span><span className="font-mono font-bold">€ {v}</span></div>
                  ))}
                </div>
              )}
            </Card>
          )}
        </aside>

        {/* Right column: Bookings */}
        <section className="lg:col-span-2">
          <PanelTable
            title={`Reservas via parceiro (${bookings.length})`}
            emptyText="Sem reservas via este parceiro ainda."
            data-testid="partner-bookings-panel"
            rows={bookings.slice(0, 50)}
            columns={[
              { key: "ref", label: "Ref.", render: (b) => <Link to={`/admin/bookings/${b.id}`} className="text-[#0a0f1e] font-mono font-semibold hover:text-[#f59e0b]">{b.reference}</Link> },
              { key: "date", label: t("date"), render: (b) => <span className="text-xs text-slate-700">{(b.service_datetime || "").slice(0, 16).replace("T", " ")}</span> },
              { key: "client", label: "Cliente", render: (b) => <span className="text-xs text-slate-700 truncate max-w-[160px] block">{b.client_name || "—"}</span> },
              { key: "route", label: "Rota", render: (b) => <span className="text-xs text-slate-700 truncate max-w-[220px] block">{b.pickup_address} → {b.destination_address}</span> },
              { key: "status", label: t("status"), render: (b) => <StatBadge status={b.status} /> },
              { key: "amt", label: "Cliente paga", render: (b) => <span className="text-sm font-bold text-[#0a0f1e]">€ {(b.client_pays ?? b.total_amount ?? 0).toFixed(2)}</span>, align: "right" },
              { key: "com", label: "Comissão", render: (b) => <span className="text-sm font-bold text-[#f59e0b]">€ {(b.partner_earning ?? b.partner_markup ?? 0).toFixed(2)}</span>, align: "right" },
            ]}
          />
        </section>

        {/* Iter 23 Sub-Fase A — Histórico mensal */}
        {data.history && (
          <PartnerHistorySection history={data.history} />
        )}
      </div>
    </div>
  );
}

function PartnerHistorySection({ history }) {
  const monthly = history.monthly || [];
  const maxRev = Math.max(1, ...monthly.map((m) => m.revenue));
  return (
    <section className="border border-slate-200 bg-white" data-testid="partner-history">
      <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
        <div className="text-[11px] uppercase tracking-widest text-slate-700 font-bold">Rendimentos & Serviços — Histórico</div>
        <div className="text-[10px] font-mono text-slate-600">
          Total: <span className="text-[#0a0f1e] font-bold">{history.total_bookings || 0}</span> reservas · comissão hotel <span className="text-[#f59e0b] font-bold">€{Number(history.total_hotel_commission || 0).toFixed(2)}</span> · rendimento parceiro <span className="text-emerald-700 font-bold">€{Number(history.total_partner_earning || 0).toFixed(2)}</span>
        </div>
      </div>
      {monthly.length === 0 ? (
        <div className="p-8 text-center text-slate-500 text-sm">Sem histórico ainda.</div>
      ) : (
        <div className="p-6 space-y-2" data-testid="partner-monthly-chart">
          {monthly.slice(0, 12).map((m) => (
            <div key={m.month} className="flex items-center gap-3">
              <div className="w-16 text-[10px] font-mono uppercase text-slate-600">{m.month}</div>
              <div className="flex-1 h-6 bg-slate-100 relative">
                <div className="h-full bg-[#f59e0b]" style={{ width: `${Math.max(2, (m.revenue / maxRev) * 100)}%` }} />
              </div>
              <div className="w-24 text-right font-mono text-xs text-[#0a0f1e]">€{m.revenue.toFixed(2)}</div>
              <div className="w-16 text-right text-[10px] text-slate-500 font-mono">{m.bookings} res.</div>
              <div className="w-24 text-right text-[10px] font-mono text-emerald-700" title="Rendimento parceiro">€{m.partner_earning.toFixed(2)}</div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function Kpi({ label, value, accent, ...props }) {
  return (
    <div className={`border ${accent ? "border-[#f59e0b] bg-[#f59e0b]/5" : "border-slate-200 bg-white"} p-4`} {...props}>
      <div className="text-[10px] uppercase tracking-widest text-slate-700 font-bold">{label}</div>
      <div className={`mt-2 text-2xl font-serif font-bold ${accent ? "text-[#f59e0b]" : "text-[#0a0f1e]"}`}>{value}</div>
    </div>
  );
}
function Card({ title, icon: Icon, children }) {
  return (
    <div className="border border-slate-200 bg-white">
      <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-2">
        {Icon && <Icon className="w-3.5 h-3.5 text-[#f59e0b]" />}
        <span className="text-[11px] uppercase tracking-widest text-slate-700 font-bold">{title}</span>
      </div>
      <div className="p-4 space-y-2">{children}</div>
    </div>
  );
}
function Row({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-2 text-sm">
      {Icon && <Icon className="w-3.5 h-3.5 text-slate-500 mt-0.5" />}
      <div className="flex-1">
        <div className="text-[10px] uppercase tracking-widest text-slate-600 font-bold">{label}</div>
        <div className="text-slate-800 mt-0.5">{value || "—"}</div>
      </div>
    </div>
  );
}
function ToggleRow({ label, hint, value, onChange, testId }) {
  return (
    <div className="border border-slate-100 p-2.5 bg-slate-50/40">
      <div className="flex items-center justify-between gap-3">
        <div className="flex-1">
          <div className="text-[11px] uppercase tracking-widest text-slate-700 font-bold">{label}</div>
          {hint && <div className="text-[10px] text-slate-500 mt-0.5 leading-snug">{hint}</div>}
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={value}
          onClick={() => onChange(!value)}
          data-testid={testId}
          className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${value ? "bg-[#f59e0b]" : "bg-slate-300"}`}
        >
          <span className={`inline-block h-5 w-5 bg-white rounded-full shadow transform transition-transform ${value ? "translate-x-5" : "translate-x-0.5"}`} />
        </button>
      </div>
    </div>
  );
}
function DocLink({ label, url }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-slate-700 font-semibold">{label}</span>
      {url ? (
        <a href={url} target="_blank" rel="noreferrer" className="text-[11px] uppercase text-[#f59e0b] hover:underline font-bold">Ver →</a>
      ) : (
        <span className="text-[11px] text-slate-500">—</span>
      )}
    </div>
  );
}
function PanelTable({ title, emptyText, rows, columns, ...props }) {
  return (
    <div className="border border-slate-200 bg-white" {...props}>
      <div className="px-5 py-3 border-b border-slate-200">
        <span className="text-[11px] uppercase tracking-widest text-slate-700 font-bold">{title}</span>
      </div>
      {rows.length === 0 ? (
        <div className="p-6 text-sm text-slate-500 text-center">{emptyText}</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-100">
              <tr>
                {columns.map((c) => (
                  <th key={c.key} className={`px-4 py-2 text-[10px] uppercase tracking-widest text-slate-700 font-bold ${c.align === "right" ? "text-right" : "text-left"}`}>{c.label}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((r, i) => (
                <tr key={r.id || i} className="hover:bg-slate-50">
                  {columns.map((c) => (
                    <td key={c.key} className={`px-4 py-2.5 ${c.align === "right" ? "text-right" : ""}`}>{c.render(r)}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


/**
 * SlugEditor — inline slug editor used inside the admin PartnerDetail page.
 *  - "Auto-gerar" button rebuilds the slug from the partner's company name.
 *  - Manual edit is validated (2-40 chars, [a-z0-9-]) before hitting the API.
 *  - Backend enforces uniqueness + reserved-word block and returns 400 on conflict.
 */
function SlugEditor({ current, companyName, onSave, onCopy }) {
  const [value, setValue] = React.useState(current || "");
  const [editing, setEditing] = React.useState(false);
  React.useEffect(() => { setValue(current || ""); }, [current]);

  const sanitize = (s) => (s || "")
    .toLowerCase()
    .normalize("NFD").replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9-]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 40);

  const generate = () => setValue(sanitize(companyName || ""));

  const save = () => {
    const clean = sanitize(value);
    if (clean.length < 2) return;
    onSave(clean);
    setEditing(false);
  };

  if (!editing) {
    return (
      <div className="flex items-center gap-2" data-testid="slug-editor-readonly">
        <code className="flex-1 text-[10px] font-mono bg-slate-50 border border-slate-200 p-2">{current || "—"}</code>
        <button onClick={() => onCopy(current)} className="text-slate-500 hover:text-[#f59e0b]" title="Copiar" data-testid="slug-copy">
          <Copy className="w-3 h-3" />
        </button>
        <button
          onClick={() => setEditing(true)}
          className="text-[10px] uppercase tracking-widest text-[#f59e0b] hover:text-amber-700 font-bold border border-[#f59e0b]/40 hover:bg-amber-50 px-2 py-1.5"
          data-testid="slug-edit-btn"
        >
          Editar
        </button>
      </div>
    );
  }

  const preview = sanitize(value);
  const changed = preview !== (current || "");
  const invalid = preview.length < 2;

  return (
    <div className="border border-[#f59e0b]/40 bg-amber-50/40 p-2 space-y-2" data-testid="slug-editor-edit">
      <input
        type="text"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        className="w-full font-mono text-xs bg-white border border-amber-300 px-2 py-1.5 focus:outline-none focus:border-[#f59e0b]"
        placeholder="ex: hotel-tivoli"
        data-testid="slug-input"
        autoFocus
      />
      <div className="text-[10px] text-slate-600 flex items-center justify-between">
        <span>
          Pré-visualização: <code className="text-[#f59e0b] font-bold">{preview || "—"}</code>
        </span>
        <button
          onClick={generate}
          className="text-[10px] uppercase tracking-widest text-slate-700 hover:text-[#f59e0b] font-bold underline"
          data-testid="slug-auto-btn"
        >
          ↻ Auto-gerar do nome
        </button>
      </div>
      {invalid && <div className="text-[10px] text-red-600">Slug deve ter pelo menos 2 caracteres.</div>}
      <div className="flex items-center gap-2 justify-end pt-1">
        <button
          onClick={() => { setEditing(false); setValue(current || ""); }}
          className="text-[10px] uppercase tracking-widest text-slate-600 hover:text-slate-900 font-bold px-2 py-1.5"
          data-testid="slug-cancel-btn"
        >
          Cancelar
        </button>
        <button
          onClick={save}
          disabled={invalid || !changed}
          className="text-[10px] uppercase tracking-widest bg-[#f59e0b] hover:bg-amber-600 disabled:opacity-40 disabled:cursor-not-allowed text-black font-bold px-3 py-1.5"
          data-testid="slug-save-btn"
        >
          Guardar
        </button>
      </div>
    </div>
  );
}
